import { AlertTriangle, Menu, SquareArrowOutUpLeft, X } from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/useAuth";
import { contactPageEnabled } from "../utils/featureFlags";
import { ObfuscatedEmail } from "./ObfuscatedEmail";

export const Header: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  // const [scrolled, setScrolled] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();


  // useEffect(() => {
  //   const handleScroll = () => {
  //     setScrolled(window.scrollY > 20);
  //   };
  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      setIsLoggingOut(true);
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="bg-gradient-to-r from-emerald-700 to-emerald-300 shadow-lg relative z-50">
      <div className="container mx-auto px-4 py-4 lg:px-16">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-start gap-1">
              <Link
                to={isAuthenticated ? "/dashboard" : "/"}
                className="group text-4xl font-bold logo-font text-white hover:underline decoration-white transition-all duration-200 relative inline-flex items-baseline"
                title={isAuthenticated ? "Go to Dashboard" : "Go to Home"}
              >
                <span>LAB ZER</span>
                <span className="relative inline-block">
                  <span className="group-hover:opacity-0 transition-opacity duration-200">0</span>
                  <SquareArrowOutUpLeft className="absolute inset-0 w-6 h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 translate-y-1" />
                </span>
              </Link>
              <span className="badge badge-sm bg-amber-300 text-emerald-700 border-amber-500">Beta</span>
            </div>
          </div>

          {/* Desktop Nav */}

          <nav className="hidden lg:flex items-center gap-10 font-semibold text-white text-base">
            {/* Common Links for Everyone */}
            <Link to="/about" className="text-primary-content hover:text-primary-content/50">
              About
            </Link>
            <Link to="/faq" className="text-primary-content hover:text-primary-content/50">
              FAQ
            </Link>
            <Link to="/partners-sponsors" className="text-primary-content hover:text-primary-content/50">
              Green Solutions
            </Link>
            {contactPageEnabled ? (
              <Link to="/contact" className="text-primary-content hover:text-primary-content/50">
                Contact
              </Link>
            ) : (
              <ObfuscatedEmail
                email="support@labzero.eco"
                displayText="Contact"
                showCopyIcon={false}
                className="text-primary-content hover:text-primary-content/50"
              />
            )}
            <button
              data-gs-form="4c3bed37-e18a-4e7b-ada0-91f827968f5a"
              className="text-primary-content hover:text-primary-content/50 cursor-pointer"
            >
              Newsletter
            </button>
            <Link
              to="https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAANAApm4H51UOUg3OEdaTjBMNEdSUE9GOU9UTTBFVDI2OC4u"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-content hover:text-primary-content/50"
            >
              Join Impact Network
            </Link>

            {!isAuthenticated && (
              <Link
                to="/login"
                className="bg-white text-emerald-700 hover:bg-emerald-50 no-underline font-semibold px-5 py-2 rounded-full transition-colors"
              >
                Login
              </Link>
            )}

            {/* Authenticated User Actions */}
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-primary-content hover:text-primary-content/50">
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex items-center gap-2 text-primary-content hover:text-primary-content/50 transition-all cursor-pointer"
                >
                  {isLoggingOut ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Logging out...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15"
                        />
                      </svg>
                      Logout
                    </>
                  )}
                </button>
              </>
            ) : null}
          </nav>


          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 rounded-lg hover:bg-black/10"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-emerald-700 text-white px-6 py-4 space-y-3 shadow-lg border-t border-emerald-600 animate-slide-down">
          {/* Common Links */}
          <Link
            to="/about"
            className="block py-2"
            onClick={() => setMenuOpen(false)}
          >
            About
          </Link>
          <Link
            to="/faq"
            className="block py-2"
            onClick={() => setMenuOpen(false)}
          >
            FAQ
          </Link>
          <Link
            to="/partners-sponsors"
            className="block py-2"
            onClick={() => setMenuOpen(false)}
          >
            Green Solutions
          </Link>
          {contactPageEnabled ? (
            <Link to="/contact" className="block py-2" onClick={() => setMenuOpen(false)}>
              Contact
            </Link>
          ) : (
            <ObfuscatedEmail
              email="support@labzero.eco"
              displayText="Contact"
              showCopyIcon={false}
              className="block py-2"
            />
          )}
          <button
            data-gs-form="4c3bed37-e18a-4e7b-ada0-91f827968f5a"
            className="block py-2 w-full text-left"
            onClick={() => setMenuOpen(false)}
          >
            Newsletter
          </button>
          <Link
            to="https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAANAApm4H51UOUg3OEdaTjBMNEdSUE9GOU9UTTBFVDI2OC4u"
            target="_blank"
            rel="noopener noreferrer"
            className="block py-2"
            onClick={() => setMenuOpen(false)}
          >
            Join Impact Network
          </Link>

          {!isAuthenticated && (
            <Link
              to="/login"
              className="block py-2 px-4 mt-2 bg-white text-emerald-700 rounded-full text-center font-semibold no-underline"
              onClick={() => setMenuOpen(false)}
            >
              Login
            </Link>
          )}

          {isAuthenticated && (
            <>
              <div className="h-px bg-emerald-600 my-2"></div>
              <Link
                to="/dashboard"
                className="block py-2 font-semibold"
                onClick={() => setMenuOpen(false)}
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center gap-2 font-medium py-2 w-full text-left"
              >
                {isLoggingOut ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    Logging out...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15"
                      />
                    </svg>
                    Logout
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Mobile viewing warning */}
      <div className="block lg:hidden bg-amber-50 border-l-4 border-amber-400 p-3">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="ml-3 text-sm text-amber-700">
            Best viewed on desktop for an optimal experience.
          </p>
        </div>
      </div>
    </header>
  );
};
